import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  FileSpreadsheet, Link2, Unlink, ArrowDownToLine, ArrowUpFromLine,
  Copy, RefreshCcw, ShieldCheck, AlertTriangle, ExternalLink, Info,
} from 'lucide-react';
import { fmtDateTime } from '@/lib/format';

const MANAGER_ROLES = ['owner', 'dm_creative', 'dm_technical', 'dm_architecture'];

/** Team Directory ↔ Google Sheets link card.
 *  Owner / DM Creative / DM Technical can:
 *    - Link a Google Sheet URL after sharing it with the service account
 *    - Pull (Sheet → App) to update matching users by email
 *    - Push (App → Sheet) to overwrite the data area with the current directory
 *  Everyone else just sees the status readonly. */
export function TeamDirectorySheetPanel() {
  const { user } = useAuth();
  const canManage = MANAGER_ROLES.includes(user?.role);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/team-directory/sheet/status');
      setStatus(r.data);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const pull = async () => {
    setBusy('pull');
    try {
      const r = await api.post('/team-directory/sheet/pull');
      toast.success(`Updated ${r.data.stats.updated} teammate${r.data.stats.updated === 1 ? '' : 's'} from the sheet.`);
      await refresh();
      // Nudge the parent to reload — a full-page reload is the safe path.
      window.dispatchEvent(new CustomEvent('team-directory-refresh'));
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(''); }
  };
  const push = async () => {
    setBusy('push');
    try {
      const r = await api.post('/team-directory/sheet/push');
      toast.success(`Pushed ${r.data.stats.rows_written} rows to the sheet.`);
      await refresh();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(''); }
  };
  const unlink = async () => {
    if (!window.confirm('Unlink this Google Sheet? The sheet itself will not be modified.')) return;
    setBusy('unlink');
    try {
      await api.delete('/team-directory/sheet/link');
      toast.success('Sheet unlinked.');
      await refresh();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(''); }
  };

  if (loading) {
    return <div className="card p-4 elev-1 text-[12px] text-[hsl(var(--foreground-muted))]" data-testid="tdsheet-loading">
      Checking Google Sheet link…
    </div>;
  }
  if (!status) return null;

  return (
    <div className="card p-4 elev-1" data-testid="tdsheet-panel">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md grid place-items-center bg-[hsl(var(--success)/.12)] text-[hsl(var(--success))] flex-none">
          <FileSpreadsheet size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13.5px] font-semibold">Google Sheet sync</div>
            {!status.configured && (
              <span className="chip status-cancelled" data-testid="tdsheet-not-configured">
                <AlertTriangle size={10} /> Service account not set up
              </span>
            )}
            {status.configured && !status.linked && (
              <span className="chip status-pending" data-testid="tdsheet-not-linked">Not linked</span>
            )}
            {status.linked && (
              <span className="chip status-done" data-testid="tdsheet-linked">
                <ShieldCheck size={10} /> Linked
              </span>
            )}
          </div>
          <p className="text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5">
            Two-way sync between this directory and a Google Sheet. Sheet is the source of truth; app edits push back on demand.
          </p>

          {/* Service account email panel */}
          {status.configured && status.service_account_email && (
            <div className="mt-3 flex items-center gap-2 flex-wrap p-2 rounded-md bg-[hsl(var(--surface-2))]"
                 data-testid="tdsheet-sa-email">
              <Info size={12} className="text-[hsl(var(--primary))]" />
              <span className="text-[11.5px] text-[hsl(var(--foreground-muted))]">Share the sheet as Editor with:</span>
              <span className="font-mono text-[12px] font-semibold">{status.service_account_email}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(status.service_account_email); toast.success('Copied'); }}
                className="text-[11px] px-1.5 py-0.5 rounded hover:bg-[hsl(var(--surface-1))] flex items-center gap-1"
                data-testid="tdsheet-copy-sa"
              >
                <Copy size={10} /> Copy
              </button>
            </div>
          )}

          {!status.configured && (
            <div className="mt-3 p-3 rounded-md bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning)/.35)]"
                 data-testid="tdsheet-setup-hint">
              <div className="flex items-start gap-2 text-[12px] text-[hsl(var(--foreground))]">
                <AlertTriangle size={13} className="text-[hsl(var(--warning))] flex-none mt-0.5" />
                <div>
                  <div className="font-semibold">The service account key isn&rsquo;t configured yet.</div>
                  <div className="text-[hsl(var(--foreground-muted))] mt-1">
                    Ask the app admin to set <span className="font-mono">GOOGLE_SERVICE_ACCOUNT_JSON_B64</span> in <span className="font-mono">backend/.env</span> and restart the backend.
                  </div>
                  <button onClick={() => setHelpOpen(true)} className="mt-1.5 text-[12px] font-semibold text-[hsl(var(--primary))] underline underline-offset-2"
                          data-testid="tdsheet-open-help">
                    Show setup steps
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Linked details */}
          {status.linked && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-[11.5px]" data-testid="tdsheet-link-info">
              <InfoRow label="Sheet"
                       value={<a href={status.link.sheet_url} target="_blank" rel="noreferrer"
                                 className="text-[hsl(var(--primary))] hover:underline flex items-center gap-1">
                                Open sheet <ExternalLink size={10} />
                              </a>} />
              <InfoRow label="Tab" value={<span className="font-mono">{status.link.tab_name}</span>} />
              <InfoRow label="Linked by" value={<span>{status.link.linked_by}</span>} />
              <InfoRow label="Last pull"
                       value={status.link.last_pull_at
                                ? <span>{fmtDateTime(status.link.last_pull_at)} · {status.link.last_pull_stats?.updated ?? 0} updated</span>
                                : <span className="italic text-[hsl(var(--foreground-subtle))]">Never</span>} />
              <InfoRow label="Last push"
                       value={status.link.last_push_at
                                ? <span>{fmtDateTime(status.link.last_push_at)} · {status.link.last_push_stats?.rows_written ?? 0} rows</span>
                                : <span className="italic text-[hsl(var(--foreground-subtle))]">Never</span>} />
              <InfoRow label="Expected headers"
                       value={<span className="font-mono text-[10.5px]">{status.expected_headers.join(', ')}</span>} />
            </div>
          )}
        </div>

        {canManage && (
          <div className="flex flex-col gap-1.5" data-testid="tdsheet-actions">
            {!status.linked ? (
              <Button size="sm" onClick={() => setLinkOpen(true)} disabled={!status.configured}
                      data-testid="tdsheet-btn-link">
                <Link2 size={13} className="mr-1.5" />Link Sheet
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={pull} disabled={!!busy} data-testid="tdsheet-btn-pull">
                  <ArrowDownToLine size={13} className="mr-1.5" />
                  {busy === 'pull' ? 'Pulling…' : 'Pull from Sheet'}
                </Button>
                <Button size="sm" variant="outline" onClick={push} disabled={!!busy} data-testid="tdsheet-btn-push">
                  <ArrowUpFromLine size={13} className="mr-1.5" />
                  {busy === 'push' ? 'Pushing…' : 'Push to Sheet'}
                </Button>
                <Button size="sm" variant="ghost" onClick={unlink} disabled={!!busy} data-testid="tdsheet-btn-unlink">
                  <Unlink size={13} className="mr-1.5" />Unlink
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={refresh} data-testid="tdsheet-btn-refresh">
              <RefreshCcw size={13} className="mr-1.5" />Refresh
            </Button>
            <button onClick={() => setHelpOpen(true)} className="text-[11px] text-[hsl(var(--primary))] underline underline-offset-2 mt-1"
                    data-testid="tdsheet-btn-help">
              How does this work?
            </button>
          </div>
        )}
      </div>

      <LinkDialog open={linkOpen} onOpenChange={setLinkOpen} onLinked={refresh}
                  serviceAccountEmail={status.service_account_email} />
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} serviceAccountEmail={status.service_account_email} />
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}

function LinkDialog({ open, onOpenChange, onLinked, serviceAccountEmail }) {
  const [url, setUrl] = useState('');
  const [tab, setTab] = useState('Sheet1');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setUrl(''); setTab('Sheet1'); setBusy(false); } }, [open]);

  const submit = async () => {
    if (!url.trim()) { toast.error('Paste a Google Sheets URL.'); return; }
    setBusy(true);
    try {
      await api.post('/team-directory/sheet/link', { url: url.trim(), tab_name: tab.trim() || 'Sheet1' });
      toast.success('Sheet linked.');
      onOpenChange(false);
      onLinked();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="tdsheet-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 size={16} className="text-[hsl(var(--primary))]" />Link a Google Sheet
          </DialogTitle>
          <DialogDescription>
            The sheet must be shared as <span className="font-semibold">Editor</span> with the service account below before you paste the URL.
          </DialogDescription>
        </DialogHeader>

        {serviceAccountEmail && (
          <div className="p-2 rounded bg-[hsl(var(--surface-2))] flex items-center gap-2 flex-wrap text-[11.5px]">
            <Info size={11} className="text-[hsl(var(--primary))]" />
            <span className="text-[hsl(var(--foreground-muted))]">Share with:</span>
            <span className="font-mono font-semibold">{serviceAccountEmail}</span>
            <button
              onClick={() => { navigator.clipboard.writeText(serviceAccountEmail); toast.success('Copied'); }}
              className="ml-auto text-[10.5px] px-1.5 py-0.5 rounded hover:bg-[hsl(var(--surface-1))] flex items-center gap-1"
              data-testid="tdsheet-link-copy-sa"
            >
              <Copy size={9} /> Copy
            </button>
          </div>
        )}

        <div className="space-y-3 pt-1">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Sheet URL</label>
            <Input value={url} onChange={e => setUrl(e.target.value)}
                   placeholder="https://docs.google.com/spreadsheets/d/…/edit"
                   data-testid="tdsheet-link-url" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Tab name</label>
            <Input value={tab} onChange={e => setTab(e.target.value)}
                   placeholder="Sheet1"
                   data-testid="tdsheet-link-tab" />
            <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))] mt-1">
              The tab that holds directory rows. Header row auto-created if the tab is empty.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="tdsheet-link-submit">
            {busy ? 'Linking…' : 'Link Sheet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HelpDialog({ open, onOpenChange, serviceAccountEmail }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] max-h-[80vh] overflow-y-auto" data-testid="tdsheet-help-dialog">
        <DialogHeader>
          <DialogTitle>How the Google Sheet sync works</DialogTitle>
          <DialogDescription>Service-account model — no per-user OAuth, no consent screens for teammates.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-[13px]">
          <div>
            <div className="font-semibold mb-1">One-time setup (app admin)</div>
            <ol className="list-decimal ml-5 space-y-1 text-[12.5px] text-[hsl(var(--foreground-muted))]">
              <li>Open <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] underline">Google Cloud Console</a> → create/select a project.</li>
              <li><strong>APIs &amp; Services → Library</strong> → search for <span className="font-mono">Google Sheets API</span> → <em>Enable</em>.</li>
              <li><strong>IAM &amp; Admin → Service Accounts</strong> → <em>Create service account</em> (e.g. <span className="font-mono">team-directory-sync</span>). No project role needed.</li>
              <li>Open the new service account → <strong>Keys → Add key → Create new key → JSON</strong>. Download the file.</li>
              <li>Base64-encode the JSON: on macOS <span className="font-mono">base64 -i key.json</span>, on Linux <span className="font-mono">base64 -w 0 key.json</span>.</li>
              <li>Paste the resulting single line into <span className="font-mono">backend/.env</span> as <span className="font-mono">GOOGLE_SERVICE_ACCOUNT_JSON_B64=…</span>.</li>
              <li>Restart the backend so the credential is picked up.</li>
            </ol>
          </div>
          <div>
            <div className="font-semibold mb-1">Every-sheet setup (Owner / Lead)</div>
            <ol className="list-decimal ml-5 space-y-1 text-[12.5px] text-[hsl(var(--foreground-muted))]">
              <li>Open the target Google Sheet.</li>
              <li>Click <strong>Share</strong> and add the service account email <span className="font-mono">{serviceAccountEmail || '(not configured yet)'}</span> as <strong>Editor</strong>.</li>
              <li>Copy the browser URL (any format — <span className="font-mono">/edit</span>, <span className="font-mono">#gid=0</span>, etc. all fine).</li>
              <li>Come back here → <strong>Link Sheet</strong> → paste the URL and confirm the tab name.</li>
            </ol>
          </div>
          <div>
            <div className="font-semibold mb-1">Sheet format</div>
            <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">
              Row 1 must have these headers (the app will auto-write them if the sheet is empty):
            </div>
            <div className="mt-1 font-mono text-[12px] bg-[hsl(var(--surface-2))] p-2 rounded">
              id · name · email · role · department · phone
            </div>
            <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1.5">
              <strong>Role</strong> must be one of: <span className="font-mono">owner, dm_creative, dm_technical, operations, accounts, commercials, hr, team</span>. Any other value is silently ignored so you can&rsquo;t accidentally break RBAC.
            </div>
            <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1">
              <strong>Empty cells</strong> during a Pull are treated as &ldquo;no change&rdquo; — they never clear an existing value. Blank a field in the app instead if that&rsquo;s the intent.
            </div>
          </div>
          <div>
            <div className="font-semibold mb-1">What the buttons do</div>
            <ul className="list-disc ml-5 space-y-1 text-[12.5px] text-[hsl(var(--foreground-muted))]">
              <li><strong>Pull from Sheet</strong> — matches rows by <span className="font-mono">email</span> and updates name / role / department / phone in the app. Unknown emails are skipped (never created, to keep auth safe).</li>
              <li><strong>Push to Sheet</strong> — overwrites the sheet&rsquo;s data area (rows 2 onward, columns A–F) with every active teammate in the app.</li>
              <li><strong>Unlink</strong> — forgets the URL. Doesn&rsquo;t touch the sheet contents.</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button size="sm" onClick={() => onOpenChange(false)}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
